import React, { useState, useEffect, useRef } from "react";
import { contasPagar, fornecedores, downloadExcel, exportData } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Plus, Search, Download, Pencil, Trash2, Upload, Loader2, FileSpreadsheet, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatCurrency, formatDate } from "../../lib/utils";

export function ContasPagarPage() {
  const [data, setData] = useState([]);
  const [fornecedoresList, setFornecedoresList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Estados para upload de extrato
  const [uploading, setUploading] = useState(false);
  const [extratoItens, setExtratoItens] = useState([]);
  const [selectedItens, setSelectedItens] = useState([]);
  const [showExtratoDialog, setShowExtratoDialog] = useState(false);
  const [confirmingExtrato, setConfirmingExtrato] = useState(false);
  const fileInputRef = useRef(null);
  
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    fornecedor_id: "",
    fornecedor_nome: "",
    categoria_financeira: "",
    centro_custo: "",
    descricao: "",
    data_lancamento: new Date().toISOString().split("T")[0],
    data_vencimento: "",
    valor_previsto: 0,
    observacoes: "",
  });

  const [pagarData, setPagarData] = useState({
    valor_pago: 0,
    data_pagamento: new Date().toISOString().split("T")[0],
  });

  const fetchData = async () => {
    try {
      const [contasRes, fornecedoresRes] = await Promise.all([
        contasPagar.list(),
        fornecedores.list(),
      ]);
      setData(contasRes.data);
      setFornecedoresList(fornecedoresRes.data);
    } catch (error) {
      toast.error("Erro ao carregar contas a pagar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await contasPagar.create(formData);
      toast.success("Conta a pagar criada!");
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar");
    }
  };

  const handlePagar = async () => {
    try {
      await contasPagar.update(editingItem.id, pagarData);
      toast.success("Pagamento registrado!");
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error("Erro ao registrar pagamento");
    }
  };

  const handleFornecedorChange = (fornecedorId) => {
    const fornecedor = fornecedoresList.find((f) => f.id === fornecedorId);
    setFormData({
      ...formData,
      fornecedor_id: fornecedorId,
      fornecedor_nome: fornecedor?.razao_social || "",
    });
  };

  const resetForm = () => {
    setFormData({
      fornecedor_id: "",
      fornecedor_nome: "",
      categoria_financeira: "",
      centro_custo: "",
      descricao: "",
      data_lancamento: new Date().toISOString().split("T")[0],
      data_vencimento: "",
      valor_previsto: 0,
      observacoes: "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta conta a pagar?")) return;
    try {
      await contasPagar.delete(id);
      toast.success("Conta excluída!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const handleExport = async () => {
    try {
      await downloadExcel(exportData.contasPagar, "contas_pagar.xlsx");
      toast.success("Exportado!");
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  // Upload de extrato bancário
  const handleExtratoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      toast.error("Apenas arquivos PDF são suportados.");
      return;
    }

    setUploading(true);
    try {
      const response = await contasPagar.uploadExtrato(file);
      const { itens, total_saidas, quantidade_itens } = response.data;
      
      if (quantidade_itens === 0) {
        toast.warning("Nenhuma transação de saída encontrada no extrato.");
        return;
      }

      setExtratoItens(itens);
      setSelectedItens(itens.map((_, idx) => idx)); // Selecionar todos por padrão
      setShowExtratoDialog(true);
      toast.success(`${quantidade_itens} transações encontradas. Total: ${formatCurrency(total_saidas)}`);
    } catch (error) {
      console.error("Erro ao processar extrato:", error);
      toast.error(error.response?.data?.detail || "Erro ao processar extrato bancário");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const toggleSelectItem = (idx) => {
    if (selectedItens.includes(idx)) {
      setSelectedItens(selectedItens.filter(i => i !== idx));
    } else {
      setSelectedItens([...selectedItens, idx]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedItens.length === extratoItens.length) {
      setSelectedItens([]);
    } else {
      setSelectedItens(extratoItens.map((_, idx) => idx));
    }
  };

  const handleConfirmExtrato = async () => {
    if (selectedItens.length === 0) {
      toast.error("Selecione pelo menos um item para importar");
      return;
    }

    setConfirmingExtrato(true);
    try {
      const itensParaImportar = selectedItens.map(idx => extratoItens[idx]);
      const response = await contasPagar.confirmarExtrato(itensParaImportar);
      toast.success(response.data.message);
      setShowExtratoDialog(false);
      setExtratoItens([]);
      setSelectedItens([]);
      fetchData();
    } catch (error) {
      toast.error("Erro ao importar lançamentos do extrato");
    } finally {
      setConfirmingExtrato(false);
    }
  };

  const filteredData = data.filter((item) => {
    const matchesSearch =
      item.fornecedor_nome?.toLowerCase().includes(search.toLowerCase()) ||
      item.descricao?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status_pagamento === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const columns = [
    { key: "descricao", header: "Descrição", render: (v) => <span className="truncate max-w-[200px] block" title={v}>{v}</span> },
    { key: "fornecedor_nome", header: "Fornecedor", render: (v) => v || <span className="text-muted-foreground">-</span> },
    { key: "categoria_financeira", header: "Categoria", render: (v) => v || <span className="text-muted-foreground">-</span> },
    { key: "data_vencimento", header: "Vencimento", render: (v) => formatDate(v) },
    { key: "valor_previsto", header: "Valor", render: (v) => formatCurrency(v) },
    { key: "valor_pago", header: "Pago", render: (v) => <span className="text-green-600">{formatCurrency(v)}</span> },
    { key: "status_pagamento", header: "Status", render: (v) => <StatusBadge status={v} /> },
    {
      key: "actions",
      header: "",
      width: "100px",
      render: (_, row) =>
        isAdmin() && (
          <div className="flex gap-1">
            {row.status_pagamento !== "pago" && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setEditingItem(row);
                  setPagarData({
                    valor_pago: row.valor_pago || 0,
                    data_pagamento: new Date().toISOString().split("T")[0],
                  });
                }}
                data-testid={`pagar-${row.id}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDelete(row.id)}
              data-testid={`delete-conta-pagar-${row.id}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
    },
  ];

  const totals = filteredData.reduce(
    (acc, item) => ({
      previsto: acc.previsto + (item.valor_previsto || 0),
      pago: acc.pago + (item.valor_pago || 0),
    }),
    { previsto: 0, pago: 0 }
  );

  const totalSelecionado = selectedItens.reduce((acc, idx) => acc + (extratoItens[idx]?.valor || 0), 0);

  return (
    <div className="space-y-6" data-testid="contas-pagar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Contas a Pagar</h1>
          <p className="text-muted-foreground">Controle de pagamentos e despesas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-contas-pagar-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          {isAdmin() && (
            <>
              {/* Upload Extrato */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleExtratoUpload}
                accept="application/pdf"
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="upload-extrato-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  <>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Importar Extrato
                  </>
                )}
              </Button>

              {/* Nova Conta Manual */}
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button data-testid="add-conta-pagar-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Conta
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nova Conta a Pagar</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Descrição *</Label>
                      <Input
                        value={formData.descricao}
                        onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                        required
                        data-testid="conta-pagar-descricao"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fornecedor</Label>
                      <Select value={formData.fornecedor_id} onValueChange={handleFornecedorChange}>
                        <SelectTrigger data-testid="conta-pagar-fornecedor">
                          <SelectValue placeholder="Selecione o fornecedor" />
                        </SelectTrigger>
                        <SelectContent>
                          {fornecedoresList.map((f) => (
                            <SelectItem key={f.id} value={f.id}>{f.razao_social}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Categoria</Label>
                        <Select value={formData.categoria_financeira} onValueChange={(v) => setFormData({ ...formData, categoria_financeira: v })}>
                          <SelectTrigger data-testid="conta-pagar-categoria">
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Fornecedor">Fornecedor</SelectItem>
                            <SelectItem value="Imposto">Imposto</SelectItem>
                            <SelectItem value="Tarifa Bancária">Tarifa Bancária</SelectItem>
                            <SelectItem value="Folha de Pagamento">Folha de Pagamento</SelectItem>
                            <SelectItem value="Serviços">Serviços</SelectItem>
                            <SelectItem value="Outros">Outros</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Centro de Custo</Label>
                        <Input
                          value={formData.centro_custo}
                          onChange={(e) => setFormData({ ...formData, centro_custo: e.target.value })}
                          data-testid="conta-pagar-centro"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Data Lançamento</Label>
                        <Input
                          type="date"
                          value={formData.data_lancamento}
                          onChange={(e) => setFormData({ ...formData, data_lancamento: e.target.value })}
                          data-testid="conta-pagar-lancamento"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Data Vencimento *</Label>
                        <Input
                          type="date"
                          value={formData.data_vencimento}
                          onChange={(e) => setFormData({ ...formData, data_vencimento: e.target.value })}
                          required
                          data-testid="conta-pagar-vencimento"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Valor *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.valor_previsto}
                        onChange={(e) => setFormData({ ...formData, valor_previsto: parseFloat(e.target.value) || 0 })}
                        required
                        data-testid="conta-pagar-valor"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                      <Button type="submit" data-testid="save-conta-pagar-btn">Criar</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-sm border p-4">
          <p className="text-sm text-muted-foreground">Total a Pagar</p>
          <p className="text-2xl font-heading font-bold">{formatCurrency(totals.previsto)}</p>
        </div>
        <div className="bg-white rounded-sm border p-4">
          <p className="text-sm text-muted-foreground">Total Pago</p>
          <p className="text-2xl font-heading font-bold text-green-600">{formatCurrency(totals.pago)}</p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-contas-pagar"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="filter-status-pagar">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="parcial">Parcial</SelectItem>
            <SelectItem value="pago">Pago</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable columns={columns} data={filteredData} loading={loading} emptyMessage="Nenhuma conta encontrada" />

      {/* Dialog de Pagamento */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pagamento</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4">
              <div className="text-sm">
                <p><span className="text-muted-foreground">Descrição:</span> {editingItem.descricao}</p>
                <p><span className="text-muted-foreground">Valor Total:</span> {formatCurrency(editingItem.valor_previsto)}</p>
              </div>
              <div className="space-y-2">
                <Label>Valor Pago</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={pagarData.valor_pago}
                  onChange={(e) => setPagarData({ ...pagarData, valor_pago: parseFloat(e.target.value) || 0 })}
                  data-testid="pagar-valor"
                />
              </div>
              <div className="space-y-2">
                <Label>Data do Pagamento</Label>
                <Input
                  type="date"
                  value={pagarData.data_pagamento}
                  onChange={(e) => setPagarData({ ...pagarData, data_pagamento: e.target.value })}
                  data-testid="pagar-data"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingItem(null)}>Cancelar</Button>
                <Button onClick={handlePagar} data-testid="confirm-pagar-btn">Confirmar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Revisão do Extrato */}
      <Dialog open={showExtratoDialog} onOpenChange={(open) => { if (!open) { setShowExtratoDialog(false); setExtratoItens([]); setSelectedItens([]); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-orange-600" />
              Revisar Lançamentos do Extrato
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-orange-50 p-3 rounded-lg border border-orange-200 text-sm">
              <p className="text-orange-800">
                <strong>Atenção:</strong> Revise os lançamentos extraídos do extrato bancário. 
                Selecione apenas os itens que deseja importar como contas a pagar.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={selectedItens.length === extratoItens.length && extratoItens.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <Label htmlFor="select-all" className="text-sm cursor-pointer">
                  Selecionar todos ({selectedItens.length}/{extratoItens.length})
                </Label>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Total selecionado:</span>{" "}
                <span className="font-bold text-orange-600">{formatCurrency(totalSelecionado)}</span>
              </div>
            </div>

            <div className="border rounded-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="p-2 w-10"></th>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">Descrição</th>
                    <th className="text-left p-2">Categoria</th>
                    <th className="text-right p-2">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {extratoItens.map((item, idx) => (
                    <tr 
                      key={idx} 
                      className={`border-t cursor-pointer hover:bg-slate-50 ${selectedItens.includes(idx) ? 'bg-orange-50' : ''}`}
                      onClick={() => toggleSelectItem(idx)}
                    >
                      <td className="p-2" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedItens.includes(idx)}
                          onCheckedChange={() => toggleSelectItem(idx)}
                        />
                      </td>
                      <td className="p-2">{formatDate(item.data)}</td>
                      <td className="p-2">
                        <span className="truncate max-w-[300px] block" title={item.descricao}>
                          {item.descricao}
                        </span>
                      </td>
                      <td className="p-2">
                        <span className="bg-slate-100 px-2 py-1 rounded text-xs">
                          {item.categoria_sugerida || "Outros"}
                        </span>
                      </td>
                      <td className="p-2 text-right font-medium text-red-600">
                        {formatCurrency(item.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowExtratoDialog(false); setExtratoItens([]); setSelectedItens([]); }}>
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmExtrato} 
                disabled={confirmingExtrato || selectedItens.length === 0}
                data-testid="confirm-extrato-btn"
              >
                {confirmingExtrato ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Importar {selectedItens.length} Lançamentos
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
